import { UserAvatar } from "@/modern/components/user-avatar";
import { Pencil } from "lucide-react";
import { cn } from "@/modern/utils";
import { NOTIFY_TYPE_COLUMN_CONTENT } from "../constants/ownershipConstants";
import { UserSelectorDialog } from "./UserSelectorDialog";
import { OverflowUsersPopover } from "./OverflowUsersPopover";
import type { BoardUser } from "@/react/hooks/entityHooks/useEntityOwnership";

interface NotificationTypeRowProps {
  notifyType: string;
  entityName: string;
  boardUserIds: number[];
  boardUsers: Array<BoardUser>;
  onSelectionChange: (notifyType: string, userIds: number[]) => void;
  className?: string;
}

export const NotificationTypeRow = ({
  notifyType,
  entityName,
  boardUserIds,
  boardUsers,
  onSelectionChange,
  className,
}: NotificationTypeRowProps) => {
  const config = NOTIFY_TYPE_COLUMN_CONTENT[notifyType as keyof typeof NOTIFY_TYPE_COLUMN_CONTENT];
  if (!config) return null;

  const Icon = config.icon;
  const assignedUsers = boardUserIds
    .map((id) => boardUsers.find((u) => u.id === id))
    .filter(Boolean) as Array<BoardUser>;

  return (
    <UserSelectorDialog
      notifyType={notifyType}
      selectedUserIds={boardUserIds}
      boardUsers={boardUsers}
      onSelectionChange={onSelectionChange}
    >
      <button
        type="button"
        className={cn(
          "w-full text-left p-3.5 rounded-xl border border-border/70 bg-card/80",
          "shadow-sm shadow-black/5",
          "transition-[border-color,background-color] duration-150 ease-out",
          "hover:border-border hover:bg-accent/40",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          "cursor-pointer",
          className,
        )}
        aria-label={`Edit ${config.title} connections`}
      >
        <div className="flex items-start gap-3">
          <span
            className="flex size-9 shrink-0 items-center justify-center rounded-lg shadow-sm"
            style={{ backgroundColor: config.iconBg }}
            aria-hidden
          >
            <Icon className="size-4 text-white" strokeWidth={2} />
          </span>

          <div className="min-w-0 flex-1 flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex flex-col gap-0.5">
                <p className="text-base font-medium text-foreground leading-snug tracking-tight">
                  {config.title}
                </p>
                <p className="text-sm text-muted-foreground leading-snug">
                  {config.description(entityName)}
                </p>
              </div>
              <span
                className="flex size-8 shrink-0 items-center justify-center text-muted-foreground"
                aria-hidden
              >
                <Pencil className="size-3.5" />
              </span>
            </div>

            <div className="flex items-center gap-2.5 min-h-7">
              {assignedUsers.length > 0 ? (
                <>
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {config.usersLabel}
                  </span>
                  <div
                    className="flex items-center -space-x-1.5"
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    {assignedUsers.slice(0, 3).map((user) => (
                      <UserAvatar key={user.id} user={user} size="small" showTooltip={true} showRemove={false} />
                    ))}
                    {assignedUsers.length > 3 && (
                      <OverflowUsersPopover assignedUsers={assignedUsers.slice(3)} />
                    )}
                  </div>
                </>
              ) : (
                <span className="text-sm text-muted-foreground italic">No users connected</span>
              )}
            </div>
          </div>
        </div>
      </button>
    </UserSelectorDialog>
  );
};
